import { groupBy, isEmpty } from 'lodash';

import { mutateGraphQL, queryGraphQL } from 'common/util/query';
import { node, nodeWithArgs, toMutation, toQuery } from 'common/util/queryHelper';
import { addApplicationMessages } from 'common/actions/applicationMessagesActions';
import { getIdRequestByTypeName } from 'workflow/utils/newInbox';
import { ACTIONS, VIEWS } from 'workflow/constants/newInbox';
import taskDetailActions from 'workflow/actions/taskDetail';

const fetchTasks = () => async dispatch => {
    dispatch(setTasksFetching(true));
    const query = toQuery(
        node('myTasksAndSignatureRequestsNewInbox', [
            'id',
            'taskId',
            'institutionId',
            'binderId',
            'typeName',
            'title',
            'actionName',
            'actionKey',
            'statement',
            'sentFrom',
            'sentBy',
            'sentDate',
            'type',
        ]),
    );

    try {
        const response = await queryGraphQL(query);
        let returnedTasks = [];
        if (response.data.myTasksAndSignatureRequestsNewInbox) {
            returnedTasks = response.data.myTasksAndSignatureRequestsNewInbox.filter(task => task !== null);
        }

        if (response.errors) {
            dispatch(
                addApplicationMessages(
                    'error',
                    ['Error retrieving some task records'],
                ),
            );
        }

        dispatch(setTasks(returnedTasks));
    } catch (error) {
        dispatch(setTasks([]));
        dispatch(addApplicationMessages('error', [error.responseText]));
    } finally {
        dispatch(setTasksFetching(false));
    }
};

const fetchTasksV4 = (status, sortOrder, queryText, filters, limit, offset) => async dispatch => {
    dispatch(setTasksFetching(true));
    const query = toQuery(
        nodeWithArgs('myTasksAndSignatureRequestsTasksV4', { status, sortOrder, queryText, filters, limit, offset }, ['totalCount', 'binderAndStudyNames',
            node('myTasksV4', [
                'id',
                'taskId',
                'institutionId',
                'binderId',
                'typeName',
                'title',
                'actionName',
                'actionKey',
                'statement',
                'sentFrom',
                'sentBy',
                'sentDate',
                'dueDate',
                'completedDate',
                'type',
                'nextTask',
                'previousTask'
            ]),

        ]),
    );

    try {
        const response = await queryGraphQL(query);
        let returnedTasks = [], tasksCount, myTasksStudyBinderNames = [];
        if (response.data.myTasksAndSignatureRequestsTasksV4) {
            myTasksStudyBinderNames = response.data.myTasksAndSignatureRequestsTasksV4.binderAndStudyNames;
            tasksCount = response.data.myTasksAndSignatureRequestsTasksV4.totalCount;
            returnedTasks = response.data.myTasksAndSignatureRequestsTasksV4.myTasksV4.filter(task => task !== null);
        }

        if (response.errors) {
            dispatch(
                addApplicationMessages(
                    'error',
                    ['Error retrieving some task records'],
                ),
            );
        }

        dispatch(setTasks(returnedTasks));
        dispatch(setTasksCount(tasksCount));
        dispatch(setMyTasksStudyBinderNames(myTasksStudyBinderNames));
    } catch (error) {
        dispatch(setTasks([]));
        dispatch(setTasksCount(''));
        dispatch(setMyTasksStudyBinderNames([]));
        dispatch(addApplicationMessages('error', [error.responseText]));
    } finally {
        dispatch(setTasksFetching(false));
    }
};

const resetState = () => ({
    type: ACTIONS.RESET_STATE
});

const setMyTasksStudyBinderNames = myTasksStudyBinderNames => ({
    type: ACTIONS.SET_MY_TASKS_STUDY_BINDER_NAMES,
    payload: myTasksStudyBinderNames
});

const setTaskWindowShowOnTaskLoad = status => ({
    type: ACTIONS.TASK_WINDOW_SHOW_ON_LOAD_TASK,
    payload: status
});


const setTaskMarkAsRead = data => async dispatch => {
    const taskId = getIdRequestByTypeName(data);
    const mutation = toMutation(
        nodeWithArgs('completeViewSdfaTask', {
            id: taskId,
        }),
    );
    try {
        const response = await mutateGraphQL(mutation);
        if (response.errors) {
            return dispatch(
                addApplicationMessages(
                    'error',
                    response.errors.map(e => e.message),
                ),
            );
        }
        dispatch({
            type: ACTIONS.MARK_TASK_AS_READ,
            payload: data,
        });
        dispatch(addApplicationMessages('success', ['Task successfully completed.']));
        return response.data.completeViewSdfaTask;
    } catch (error) {
        dispatch(addApplicationMessages('error', [error.responseText]));
    }
};

const submitSignTasks = (tasks, signingToken, isV4Tasks = false, callBackOnSuccess) => async dispatch => {
    dispatch(setTasksSigning(true));
    const eventAndDoaNodes = [];
    const taskGroups = groupBy(tasks, 'typeName');

    if (!isEmpty(taskGroups.event)) {
        const events = taskGroups.event.map((t) => {
            return {
                eventId: t.id,
                taskId: t.taskId,
                institutionId: t.institutionId,
                statement: t.statement,
                signingToken,
            };
        });
        const eventNode = nodeWithArgs('signMultipleEvents', {
            inputs: events,
        });
        eventAndDoaNodes.push(eventNode);
    }

    if (!isEmpty(taskGroups.doa)) {
        const events = taskGroups.doa.map((t) => {
            return {
                delegationId: t.id,
                taskId: t.taskId,
                binderId: t.binderId,
                institutionId: t.institutionId,
                statement: t.statement,
                signingToken,
            };
        });
        const doaNode = nodeWithArgs('signMultipleDOATasks', {
            inputs: events,
        });
        eventAndDoaNodes.push(doaNode);
    }

    const eventAndDoaResponse = isEmpty(eventAndDoaNodes) ? {} : await mutateGraphQL(toMutation(eventAndDoaNodes));

    // Intentionally call sdfa requests sequentially
    // since the BE does not handle multi-sign multiple sdfa requests of the same document
    const sdfas = taskGroups.task || [];
    const sdfaResponses = [];
    for (let i = 0; i < sdfas.length; i++) {
        const sdfaNode = nodeWithArgs('completeSignSdfaMultipleTasks', {
            ids: [sdfas[i].id],
            signingToken,
        });
        const sdfaResponse = await mutateGraphQL(toMutation([sdfaNode]));
        sdfaResponses.push(sdfaResponse);
    }

    const errors = [eventAndDoaResponse, ...sdfaResponses]
        .flatMap(response => response.errors)
        .filter(response => response);

    if (isEmpty(errors)) {
        dispatch(submitSignTasksCompletion(true));
        dispatch(setTasksSigning(false));
        const signSuccessMessage = tasks.length === 1
            ? 'Task successfully completed.'
            : 'Tasks successfully completed.';
        dispatch(addApplicationMessages('success', [signSuccessMessage]));

        if (!isV4Tasks)
            dispatch(fetchTasks());
        else
            callBackOnSuccess();

        dispatch(changeView(VIEWS.TASK_LIST));
    } else {
        dispatch(addApplicationMessages('error', errors.map(e => e.message)));
        dispatch(submitSignTasksCompletion(false));
        dispatch(setTasksSigning(false));
    }
};

const setTasks = tasks => ({
    type: ACTIONS.SET_TASKS,
    payload: tasks
});
const setTasksCount = tasksCount => ({
    type: ACTIONS.SET_TASKS_COUNT,
    payload: tasksCount
});

const setTasksFetching = isFetching => ({
    type: ACTIONS.SET_TASKS_FETCHING,
    payload: isFetching
});

const setTasksSigning = isSigning => ({
    type: ACTIONS.SET_TASKS_SIGNING,
    payload: isSigning
});

const setTaskForDetailDisplay = task => ({
    type: ACTIONS.SET_TASK_FOR_DETAIL_DISPLAY,
    payload: task
});

const toggleTaskForMultiSign = task => ({
    type: ACTIONS.TOGGLE_TASK_FOR_MULTI_SIGN,
    payload: task
});

const setTaskForSingleSign = task => ({
    type: ACTIONS.SET_TASK_FOR_SINGLE_SIGN,
    payload: task
});

const submitSignTasksCompletion = success => ({
    type: ACTIONS.SUBMIT_SIGN_TASKS_COMPLETION,
    payload: success,
});

const changeView = viewId => dispatch => {
    if (viewId === VIEWS.TASK_LIST) {
        dispatch(setTaskForSingleSign({}));
        dispatch(taskDetailActions.clearTaskDetail());
    }
    dispatch({
        type: ACTIONS.CHANGE_VIEW,
        payload: viewId
    });
};

const setTaskWindowDetail = task => ({
    type: ACTIONS.TASK_WINDOW_DETAIL,
    payload: task
});

const newInboxActions = {
    changeView,
    fetchTasks,
    fetchTasksV4,
    resetState,
    setTaskMarkAsRead,
    setTasks,
    setTasksFetching,
    setTaskForDetailDisplay,
    toggleTaskForMultiSign,
    setTaskForSingleSign,
    submitSignTasks,
    submitSignTasksCompletion,
    setTaskWindowDetail,
    setTaskWindowShowOnTaskLoad,
    setTasksCount
};

export default newInboxActions;
